import { useCallback, useEffect, useRef } from 'react'

export type KeyboardShortcut = {
  key: string
  ctrl?: boolean
  alt?: boolean
  shift?: boolean
  meta?: boolean
  preventDefault?: boolean
  stopPropagation?: boolean
}

export type ShortcutMapping = Record<string, () => void>

export type KeyboardShortcutOptions = {
  // Whether shortcuts should be disabled when user is typing in inputs
  respectInputs?: boolean
  // Whether shortcuts should be disabled when component/element is disabled
  respectDisabled?: boolean
  // Scope shortcuts to a specific element (default: document)
  scope?: React.RefObject<HTMLElement>
  // Whether shortcuts are currently enabled
  enabled?: boolean
}

export interface KeyboardShortcutActions {
  parseShortcut: (shortcut: string) => KeyboardShortcut
  matchesShortcut: (event: KeyboardEvent, shortcut: KeyboardShortcut) => boolean
  shouldIgnoreShortcut: (event: KeyboardEvent) => boolean
}

/**
 * Custom hook for managing keyboard shortcuts with comprehensive edge case handling
 *
 * @param shortcuts - Object mapping key combinations to callback functions
 * @param options - Configuration options for shortcut behavior
 *
 * @example
 * ```tsx
 * useKeyboardShortcuts({
 *   '1': () => toggleSidebar(),
 *   'ctrl+r': () => resetFilters(),
 *   'escape': () => closeModal(),
 *   'ctrl+shift+d': () => toggleDebug(),
 * }, {
 *   respectInputs: true,
 *   enabled: !isLoading
 * })
 * ```
 */
export const useKeyboardShortcuts = (
  shortcuts: ShortcutMapping,
  options: KeyboardShortcutOptions = {},
): KeyboardShortcutActions => {
  const { scope, enabled = true } = options

  // Store callbacks in ref to avoid recreating event listeners
  const shortcutsRef = useRef<ShortcutMapping>(shortcuts)
  const optionsRef = useRef<KeyboardShortcutOptions>(options)

  // Update refs when props change
  useEffect(() => {
    shortcutsRef.current = shortcuts
    optionsRef.current = options
  }, [shortcuts, options])

  /**
   * Parse a shortcut string into its components
   * e.g., "ctrl+shift+a" -> { key: 'a', ctrl: true, shift: true }
   */
  const parseShortcut = useCallback((shortcut: string): KeyboardShortcut => {
    const parts = shortcut.toLowerCase().split('+')
    const key = parts[parts.length - 1] ?? ''

    return {
      key,
      ctrl: parts.includes('ctrl'),
      alt: parts.includes('alt'),
      shift: parts.includes('shift'),
      meta: parts.includes('meta') || parts.includes('cmd'),
      preventDefault: true,
      stopPropagation: true,
    }
  }, [])

  /**
   * Check if the pressed key matches a shortcut definition
   */
  const matchesShortcut = useCallback(
    (event: KeyboardEvent, shortcut: KeyboardShortcut): boolean => {
      // Chrome autofill dispatches synthetic keydown events with no `key`.
      if (typeof event.key !== 'string') return false
      const eventKey = event.key.toLowerCase()
      const shortcutKey = shortcut.key.toLowerCase()

      // Handle special key mappings
      const keyMap: Record<string, string> = {
        escape: 'escape',
        esc: 'escape',
        enter: 'enter',
        return: 'enter',
        space: ' ',
        spacebar: ' ',
        tab: 'tab',
        backspace: 'backspace',
        delete: 'delete',
        del: 'delete',
      }

      const normalizedEventKey = keyMap[eventKey] || eventKey
      const normalizedShortcutKey = keyMap[shortcutKey] || shortcutKey

      return (
        normalizedEventKey === normalizedShortcutKey &&
        !!event.ctrlKey === !!shortcut.ctrl &&
        !!event.altKey === !!shortcut.alt &&
        !!event.shiftKey === !!shortcut.shift &&
        !!event.metaKey === !!shortcut.meta
      )
    },
    [],
  )

  /**
   * Check if shortcuts should be ignored based on current context
   */
  const shouldIgnoreShortcut = useCallback(
    (event: KeyboardEvent): boolean => {
      const currentOptions = optionsRef.current

      // If shortcuts are disabled, ignore
      if (!enabled) return true

      // A focus-scoped region can opt out of global shortcuts by tagging its
      // root with data-shortcut-scope. Focus-scoped wins over global — e.g. an
      // Ask Yogi form widget that binds 1-9 to option rows must not lose those
      // keys to a navbar/sidebar shortcut. Checked unconditionally so callers
      // don't need to opt into respectInputs to honor the scope.
      const rawTarget = event.target
      let scoped: Element | null =
        rawTarget instanceof Element ? rawTarget : null
      while (scoped) {
        if (scoped.hasAttribute('data-shortcut-scope')) return true
        scoped = scoped.parentElement
      }

      // If respectInputs is true, ignore shortcuts when user is typing
      if (currentOptions.respectInputs) {
        const target = event.target as HTMLElement
        const tagName = target.tagName.toLowerCase()
        const isContentEditable = target.contentEditable === 'true'
        const isInput = ['input', 'textarea', 'select'].includes(tagName)

        if (isInput || isContentEditable) {
          return true
        }

        // Also check for any element with role="textbox"
        if (target.getAttribute('role') === 'textbox') {
          return true
        }
      }

      // If respectDisabled is true, check if the target or any parent is disabled
      if (currentOptions.respectDisabled) {
        let element: HTMLElement | null = event.target as HTMLElement
        while (element) {
          if (
            element.hasAttribute('disabled') ||
            element.getAttribute('aria-disabled') === 'true'
          ) {
            return true
          }
          element = element.parentElement
        }
      }

      return false
    },
    [enabled],
  )

  /**
   * Handle keydown events and execute matching shortcuts
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Skip if we should ignore shortcuts in current context
      if (shouldIgnoreShortcut(event)) {
        return
      }

      const currentShortcuts = shortcutsRef.current

      // Check each registered shortcut
      for (const [shortcutString, callback] of Object.entries(
        currentShortcuts,
      )) {
        const shortcut = parseShortcut(shortcutString)

        if (matchesShortcut(event, shortcut)) {
          // Prevent default behavior and stop propagation if configured
          if (shortcut.preventDefault) {
            event.preventDefault()
          }
          if (shortcut.stopPropagation) {
            event.stopPropagation()
          }

          // Execute the callback
          try {
            callback()
          } catch {
            /* intentionally empty */
          }

          // Only execute the first matching shortcut
          break
        }
      }
    },
    [parseShortcut, matchesShortcut, shouldIgnoreShortcut],
  )

  // Set up event listeners
  useEffect(() => {
    const target = scope?.current || document

    if (!target || !enabled) {
      return
    }

    target.addEventListener('keydown', handleKeyDown as EventListener)

    return () => {
      target.removeEventListener('keydown', handleKeyDown as EventListener)
    }
  }, [handleKeyDown, scope, enabled])

  // Return utility functions for advanced use cases
  return {
    parseShortcut,
    matchesShortcut,
    shouldIgnoreShortcut,
  }
}

/**
 * Utility function to format shortcut strings for display
 * e.g., "ctrl+shift+a" -> "Ctrl+Shift+A"
 */
export const formatShortcutForDisplay = (shortcut: string): string => {
  return shortcut
    .split('+')
    .map((part) => {
      const formatted =
        part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
      // Handle special cases
      const specialKeys: Record<string, string> = {
        Ctrl: '⌃',
        Alt: '⌥',
        Shift: '⇧',
        Meta: '⌘',
        Cmd: '⌘',
        Enter: '↵',
        Escape: '⎋',
        Space: '␣',
        Tab: '⇥',
        Backspace: '⌫',
        Delete: '⌦',
      }
      return specialKeys[formatted] || formatted
    })
    .join('')
}

/**
 * Hook to get formatted shortcut display for tooltips
 */
export const useShortcutDisplay = (shortcut: string): string => {
  return formatShortcutForDisplay(shortcut)
}
